import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/context/AuthContext'
import Layout from '@/components/Layout'
import LoginPage from '@/pages/LoginPage'
import NewsPage from '@/pages/NewsPage'
import NewsFormPage from '@/pages/NewsFormPage'
import NewsDetailPage from '@/pages/NewsDetailPage'
import MarqueePage from '@/pages/MarqueePage'
import GroupsPage from '@/pages/GroupsPage'
import GroupFormPage from '@/pages/GroupFormPage'
import GroupDetailPage from '@/pages/GroupDetailPage'
import MembersPage from '@/pages/MembersPage'
import MemberFormPage from '@/pages/MemberFormPage'
import MemberDetailPage from '@/pages/MemberDetailPage'
import DiscographyFormPage from '@/pages/DiscographyFormPage'
import DiscographyDetailPage from '@/pages/DiscographyDetailPage'
import ScheduleFormPage from '@/pages/ScheduleFormPage'
import ScheduleDetailPage from '@/pages/ScheduleDetailPage'
import MediaFormPage from '@/pages/MediaFormPage'
import MediaDetailPage from '@/pages/MediaDetailPage'
import DiscographyPage from '@/pages/DiscographyPage'
import SchedulePage from '@/pages/SchedulePage'
import MediaPage from '@/pages/MediaPage'
import DashboardPage from '@/pages/DashboardPage'
import AssetsPage from '@/pages/AssetsPage'
import SaleEventsPage from '@/pages/SaleEventsPage'
import SaleEventFormPage from '@/pages/SaleEventFormPage'
import SaleEventDetailLayout from '@/pages/SaleEventDetailLayout'
import SaleEventOverviewPage from '@/pages/SaleEventOverviewPage'
import SaleEventPhasesPage from '@/pages/SaleEventPhasesPage'
import SaleEventOrdersPage from '@/pages/SaleEventOrdersPage'
import SaleOrderDetailPage from '@/pages/SaleOrderDetailPage'
import SaleEventCheckinPage from '@/pages/SaleEventCheckinPage'
import SalesHubLayout from '@/pages/SalesHubLayout'
import SalesHubLandingPage from '@/pages/SalesHubLandingPage'
import SettingsPage from '@/pages/SettingsPage'
import CheckinOperatorPage from '@/pages/CheckinOperatorPage'
import TenantsPage from '@/pages/TenantsPage'
import TenantFormPage from '@/pages/TenantFormPage'
import TenantDetailLayout from '@/pages/TenantDetailLayout'
import TenantOverviewPage from '@/pages/TenantOverviewPage'
import TenantUsersPage from '@/pages/TenantUsersPage'

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { isGroupManager } = useAuth()
  return isGroupManager ? <Navigate to="/" replace /> : <>{children}</>
}

function AppRoutes() {
  const { token, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <span className="text-zinc-500 text-sm">Loading…</span>
      </div>
    )
  }

  // Public route — operator check-in doesn't need admin auth
  if (location.pathname.startsWith('/checkin/')) {
    return (
      <Routes>
        <Route path="/checkin/:token" element={<CheckinOperatorPage />} />
      </Routes>
    )
  }

  if (!token) return <LoginPage />

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<DashboardPage />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/news/create" element={<NewsFormPage />} />
        <Route path="/news/:id" element={<NewsDetailPage />} />
        <Route path="/news/:id/edit" element={<NewsFormPage />} />
        <Route path="/marquee" element={<MarqueePage />} />
        <Route path="/groups" element={<GroupsPage />} />
        <Route path="/groups/create" element={<RequireAdmin><GroupFormPage /></RequireAdmin>} />
        <Route path="/groups/:id" element={<GroupDetailPage />} />
        <Route path="/groups/:id/edit" element={<GroupFormPage />} />
        <Route path="/members" element={<MembersPage />} />
        <Route path="/members/create" element={<MemberFormPage />} />
        <Route path="/members/:id" element={<MemberDetailPage />} />
        <Route path="/members/:id/edit" element={<MemberFormPage />} />
        <Route path="/discography" element={<DiscographyPage />} />
        <Route path="/discography/create" element={<DiscographyFormPage />} />
        <Route path="/discography/:id" element={<DiscographyDetailPage />} />
        <Route path="/discography/:id/edit" element={<DiscographyFormPage />} />
        <Route path="/schedule" element={<SchedulePage />} />
        <Route path="/schedule/create" element={<ScheduleFormPage />} />
        <Route path="/schedule/:id" element={<ScheduleDetailPage />} />
        <Route path="/schedule/:id/edit" element={<ScheduleFormPage />} />
        <Route path="/media" element={<MediaPage />} />
        <Route path="/media/create" element={<MediaFormPage />} />
        <Route path="/media/:id" element={<MediaDetailPage />} />
        <Route path="/media/:id/edit" element={<MediaFormPage />} />
        <Route path="/assets" element={<AssetsPage />} />
        <Route path="/settings" element={<RequireAdmin><SettingsPage /></RequireAdmin>} />
        <Route path="*" element={<Navigate to="/news" replace />} />
      </Route>

      {/* Sales Hub — separate layout, no main sidebar */}
      <Route path="/sales" element={<RequireAdmin><SalesHubLayout /></RequireAdmin>}>
        <Route index element={<SalesHubLandingPage />} />
        <Route path="sale-events" element={<SaleEventsPage />} />
        <Route path="sale-events/create" element={<SaleEventFormPage />} />
        <Route path="orders/:orderId" element={<SaleOrderDetailPage />} />
        <Route path="sale-events/:id/edit" element={<SaleEventFormPage />} />
        <Route path="sale-events/:id" element={<SaleEventDetailLayout />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<SaleEventOverviewPage />} />
          <Route path="phases" element={<SaleEventPhasesPage />} />
          <Route path="orders" element={<SaleEventOrdersPage />} />
          <Route path="checkin" element={<SaleEventCheckinPage />} />
        </Route>
        <Route path="tenants" element={<TenantsPage />} />
        <Route path="tenants/create" element={<TenantFormPage />} />
        <Route path="tenants/:id/edit" element={<TenantFormPage />} />
        <Route path="tenants/:id" element={<TenantDetailLayout />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<TenantOverviewPage />} />
          <Route path="users" element={<TenantUsersPage />} />
        </Route>
      </Route>
    </Routes>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  )
}
